import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Loader2, Plus, Pencil, Trash2, ExternalLink, Sparkles, Landmark, Users, Award, Search, Upload, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';

const CAT_ICON = { association: Users, local_authority: Landmark, awards: Award };
const CAT_COLOR = {
  association: 'bg-sky-100 text-sky-700',
  local_authority: 'bg-emerald-100 text-emerald-700',
  awards: 'bg-amber-100 text-amber-700',
};
const EMPTY = { name: '', category: 'association', scope: 'national', region: '', website: '', description: '' };

const CuratedLists = () => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin || user?.role === 'owner' || user?.role === 'admin';
  const navigate = useNavigate();
  const [crmBusy, setCrmBusy] = useState('');
  const openInCrm = async (o) => {
    setCrmBusy(o.id);
    try {
      const { data } = await axios.post(`${API}/curated-lists/${o.id}/add-to-crm`);
      if (data.created) toast.success(`${o.name} added to the CRM`);
      navigate(`/admin/companies?open=${data.company_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add to CRM'); }
    finally { setCrmBusy(''); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/curated-lists/upload`, fd);
      toast.success(`Uploaded: ${data.added} added, ${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  const [meta, setMeta] = useState({ categories: {}, counts: {}, total: 0, regions: [] });
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadMeta = useCallback(async () => {
    try { const r = await axios.get(`${API}/curated-lists/meta`); setMeta(r.data); } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== ALL) params.category = category;
      if (search.trim()) params.search = search.trim();
      const r = await axios.get(`${API}/curated-lists`, { params });
      setOrgs(r.data.items || []);
    } finally { setLoading(false); }
  }, [category, search]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...EMPTY, ...o }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, region: form.region || null, website: form.website || null, description: form.description || null };
      if (editing) await axios.patch(`${API}/curated-lists/${editing.id}`, payload);
      else await axios.post(`${API}/curated-lists`, payload);
      toast.success(editing ? 'Updated' : 'Added');
      setDialogOpen(false); load(); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.name}"?`)) return;
    try { await axios.delete(`${API}/curated-lists/${o.id}`); toast.success('Deleted'); load(); loadMeta(); }
    catch { toast.error('Delete failed'); }
  };

  const seed = async () => {
    setSeeding(true);
    try { const r = await axios.post(`${API}/curated-lists/seed-defaults`); toast.success(`Seeded, ${r.data.created} added (${r.data.total} total)`); load(); loadMeta(); }
    catch { toast.error('Seeding failed'); }
    finally { setSeeding(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="curated-lists-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Curated Lists</h1>
          <p className="text-gray-500 text-sm mt-1">Internal reference: UK care associations, local authority bodies and awards organisations. For targeting, partnerships & sponsorship planning.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {meta.total === 0 && (
              <Button variant="outline" onClick={seed} disabled={seeding} data-testid="curated-seed-btn">
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Seed starter set
              </Button>
            )}
            <label className="inline-flex items-center gap-2 cursor-pointer border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:border-[#00AEEF]" title="CSV columns: name, category, scope, region, website, description" data-testid="curated-upload-btn">
              <Upload className="w-4 h-4" /> Upload CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="curated-upload-input" />
            </label>
            <Button onClick={openCreate} className="bg-[#00AEEF] hover:bg-[#008CBE]" data-testid="curated-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Add organisation
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {Object.entries(meta.categories).map(([key, lbl]) => {
          const Icon = CAT_ICON[key] || Users;
          return (
            <Card key={key} className="cursor-pointer" onClick={() => setCategory(category === key ? ALL : key)} data-testid={`curated-stat-${key}`}>
              <CardContent className={`p-5 flex items-center gap-4 ${category === key ? 'ring-2 ring-[#00AEEF] rounded-lg' : ''}`}>
                <div className="w-11 h-11 rounded-lg bg-[#00AEEF]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#00AEEF]" /></div>
                <div><p className="text-2xl font-bold text-gray-900">{meta.counts[key] ?? 0}</p><p className="text-sm text-gray-500">{lbl}</p></div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Search organisations…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="curated-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-56" data-testid="curated-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All categories</SelectItem>
            {Object.entries(meta.categories).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : orgs.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400">
          <p className="text-sm">No organisations yet.</p>
          {isAdmin && meta.total === 0 && <Button variant="outline" onClick={seed} disabled={seeding} className="mt-4">{seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Seed the researched starter set</Button>}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {orgs.map((o) => (
            <Card key={o.id} data-testid={`curated-row-${o.id}`}><CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => openInCrm(o)} className="font-semibold text-gray-900 hover:text-[#00AEEF] text-left" data-testid={`curated-open-${o.id}`}>{o.name}</button>
                  <Badge variant="secondary" className={CAT_COLOR[o.category]}>{meta.categories[o.category] || o.category}</Badge>
                  {o.scope && <span className="text-xs text-gray-400 capitalize">{o.scope}{o.region ? ` · ${o.region}` : ''}</span>}
                </div>
                {o.description && <p className="text-sm text-gray-500 mt-1">{o.description}</p>}
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" onClick={() => openInCrm(o)} disabled={crmBusy === o.id} data-testid={`curated-crm-${o.id}`}>
                  {crmBusy === o.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Building2 className="w-3.5 h-3.5 mr-1" />}{o.crm_company_id ? 'Open record' : 'Add to CRM'}
                </Button>
                {o.website && <a href={o.website} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-[#00AEEF] p-2" data-testid={`curated-web-${o.id}`}><ExternalLink className="w-4 h-4" /></a>}
                {isAdmin && <>
                  <button onClick={() => openEdit(o)} className="text-gray-400 hover:text-gray-700 p-2" data-testid={`curated-edit-${o.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(o)} className="text-gray-400 hover:text-red-600 p-2" data-testid={`curated-delete-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                </>}
              </div>
            </CardContent></Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="curated-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit organisation' : 'Add organisation'}</DialogTitle>
            <DialogDescription>Reference data for internal targeting & partnerships.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="curated-form-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="curated-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(meta.categories).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Scope</Label>
                <Select value={form.scope || 'national'} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="curated-form-scope"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="national">National</SelectItem><SelectItem value="regional">Regional</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Region (optional)</Label><Input value={form.region || ''} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="curated-form-region" /></div>
            <div><Label>Website (optional)</Label><Input value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="curated-form-website" /></div>
            <div><Label>Description (optional)</Label><Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="curated-form-description" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#00AEEF] hover:bg-[#008CBE]" data-testid="curated-form-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CuratedLists;
